import { useCallback, useRef, useState } from 'react';
import { resolveUploadUrl, uploadOneFile } from '../../features/uploads/index.js';
import { useAuth } from '../../auth/AuthProvider.js';

export type AvatarUploadStatus = 'idle' | 'uploading' | 'processing' | 'ready' | 'error';

export interface UseAvatarUploadResult {
  status: AvatarUploadStatus;
  progress: number;
  /** Set once the upload lands AND the ingest pipeline has already assigned
   * a `files` row (see doc comment below) — the id to pass as
   * `settingsProfileUpdateInput.avatarFileId`. */
  fileId: string | null;
  errorMessage: string | undefined;
  upload: (file: File) => Promise<void>;
  reset: () => void;
}

/**
 * F10 avatar upload. Reuses the ONE upload transport this codebase has
 * (`POST /upload`, F18/§5.8 — `features/uploads/uploadTransport.ts`) rather
 * than inventing a second upload path for avatars specifically.
 *
 * KNOWN GAP (flagged for the Integrate stage, see this task's WIRING.md):
 * `uploadSchema.fileId` is set asynchronously by the `ingest-worker`'s
 * `register_file` checkpoint (F18), not by the `POST /upload` response
 * itself — for a document upload the caller just watches `job:{jobId}`.
 * There is no equivalent per-upload WS channel/poll op in this task's
 * ownership to learn when a *specific* upload's `fileId` becomes non-null.
 * When the immediate response already carries a `fileId` (e.g. a future
 * synchronous avatar-specific endpoint), this resolves straight to `ready`.
 * When it doesn't, this surfaces a `processing` state and stops — the
 * caller (`ProfileSection`) shows "processing" copy and leaves Save
 * disabled for the avatar change until a real signal exists.
 */
export function useAvatarUpload(): UseAvatarUploadResult {
  const { accessToken } = useAuth();
  const [status, setStatus] = useState<AvatarUploadStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [fileId, setFileId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setStatus('idle');
    setProgress(0);
    setFileId(null);
    setErrorMessage(undefined);
  }, []);

  const upload = useCallback(
    async (file: File): Promise<void> => {
      if (!accessToken) {
        setStatus('error');
        setErrorMessage('errors:code.UNAUTHENTICATED');
        return;
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setStatus('uploading');
      setProgress(0);
      setErrorMessage(undefined);

      try {
        const response = await uploadOneFile({
          url: resolveUploadUrl(),
          accessToken,
          file,
          folderId: null,
          onProgress: (event) => setProgress(event.total > 0 ? event.loaded / event.total : 0),
          signal: controller.signal,
        });
        const result = response.files[0];
        if (!result || !result.ok) {
          setStatus('error');
          setErrorMessage(result?.error.message ?? 'errors:code.INTERNAL');
          return;
        }
        if (result.upload.fileId) {
          setFileId(result.upload.fileId);
          setStatus('ready');
        } else {
          setStatus('processing');
        }
      } catch {
        setStatus('error');
        setErrorMessage('errors:code.INTERNAL');
      }
    },
    [accessToken],
  );

  return { status, progress, fileId, errorMessage, upload, reset };
}
